import styles from './ccf-agent-state.module.scss';
import React, { useEffect, useRef, useState, useTransition } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectStatus,
  selectcurrentStatus,
  agentStateActions,
} from './ccf-agent-state.slice';
import {
  CcfUnavailableIcon,
  CcfAvailableIcon,
  CcfLogoutIcon,
  CcfFavouriteIcon,
  CcfNotFavoriteIcon,
  CcfWorkingIcon,
  CcfAvatar,
  CcfBadge,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  loginActions,
  selectLoginCredentialsState,
} from '../login/+state/login.slice';
import {
  Status,
  globalActions,
  voicePreferenceActions,
} from '@nice-ccf/ui-components';
import { CcfTypography } from '@nice-ccf/ui-controls';
import { formatTimer } from '../../util/common';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { isEternalsEnv } from '../../util/common';

/* eslint-disable-next-line */
export interface CcfAgentStateProps {
  agentStateForEmbeededView: boolean;
}

/**
 * Component displays popup with all code for agent state
 * @param props -CcfAgentStateProps
 * @returns popup component with all code for agent state
 * @example <CcfAgentState/>
 */
export function CcfAgentState(props?: CcfAgentStateProps) {
  const agentStateStatus = useSelector(selectStatus);
  const agentCurrentStatus = useSelector(selectcurrentStatus);
  const { setCurrentStatus, updateFavourite } = agentStateActions;
  const dispatch = useDispatch();

  const [showGoAvailableButton, setGoAvailableButton] = useState(true);
  const [showAllUnavailableStatus, setAllUnavailableStatus] = useState(false);
  const {setAuthenticationStage} = globalActions;
  const timerIdRef = useRef(0);
  const [timer, setTimer] = useState(0);
  const AVAILABLE = 'available';
  const UNAVAILABLE = 'unavailable';
  const WORKINGOB = 'WorkingOB';
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const initials = isAuthenticated
    ? loginCredentialsArray[0].charAt(0).toUpperCase() +
      loginCredentialsArray[1].charAt(0).toUpperCase()
    : loginCredentials.firstName.charAt(0).toUpperCase() +
      loginCredentials.lastName.charAt(0).toUpperCase();
  const { logOutUser } = loginActions;
  const { setredirectToDashboardOnLogout } = voicePreferenceActions;
  const [translate] = useTranslator();
  useEffect(() => {
    timerIdRef.current = parseInt(
      setInterval(() => setTimer((c) => c + 1), 1000).toString()
    );
    return () => {
      clearInterval(timerIdRef.current);
    };
  }, [timer]);

  useEffect(() => {
    setTimer(0);
  }, [agentCurrentStatus]);

  /**
   * Function to handle status selection of agent
   * @param state - any
   * @param action -PayloadAction<string>
   * @example - updateFavourite(state, action)
   */
  const handleStatusSelection = (status: string) => {
    dispatch(setCurrentStatus(status));
    setGoAvailableButton(true);
    setAllUnavailableStatus(false);
  };
  /**
   * Function to handle status display of agent
   * @example handleStatusDisplay()
   */
  const handleStatusDisplay = () => {
    setGoAvailableButton(true);
    setAllUnavailableStatus(false);
    dispatch(
      setCurrentStatus(
        agentCurrentStatus === AVAILABLE ? UNAVAILABLE : AVAILABLE
      )
    );
  };

  const sortedStatus = agentStateStatus.allStatus
    .slice()
    .sort((a: Status, b: Status) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0
    );
  /**
   * Function to logoutUser from app
   * @param agentState - boolean
   * @example handleAgentState(true)
   * @returns
   */
  const signOutUser = () => {
    if (isAuthenticated) {
      instance.logoutRedirect({
        postLogoutRedirectUri: '/',
      });
      if (!isEternalsEnv()) {
        dispatch(setAuthenticationStage(1));
      }     
      localStorage.removeItem('interactionHistoryLS'); //Remove interactions history from local storgae after logout
    } else {
      dispatch(logOutUser(false));
      dispatch(setredirectToDashboardOnLogout());
    }
  };

  /**
   * Function to handle agent state
   * @param agentState - boolean
   * @example handleAgentState(true)
   * @returns
   */
  const handleAgentState = (agentState: boolean) => {
    return (event: React.MouseEvent) => {
      setAllUnavailableStatus(agentState);
      agentCurrentStatus !== AVAILABLE && setGoAvailableButton(!agentState);
    };
  };

  /**
   * Function to handle icon for status
   * @param status - string
   * @example handleIconForStatus()
   * @returns
   */
  const handleIconForStatus = (status: string) => {
    switch (status) {
      case AVAILABLE:
        return (
          <CcfAvailableIcon className="iconAvailable" viewBox="0 0 22 22" />
        );
        break;
      case WORKINGOB:
        return <CcfWorkingIcon className="iconAvailable" viewBox="0 0 22 22" />;
        break;
      case UNAVAILABLE:
        return (
          <CcfUnavailableIcon className="iconUnavailable" viewBox="0 0 22 22" />
        );
        break;
      default:
        return (
          <CcfUnavailableIcon className="iconUnavailable" viewBox="0 0 22 22" />
        );
        break;
    }
    // return status !== AVAILABLE ? (
    //   <CcfUnavailableIcon className="iconUnavailable" viewBox="0 0 22 22" />
    // ) : (
    //   <CcfAvailableIcon className="iconAvailable" viewBox="0 0 22 22" />
    // );
  };

  const filteredFavStatus = sortedStatus.filter((fav) => fav.isFavourite);

  return (
    <section
      id="agentStateBlock"
      className={styles.agentStatusSelection}
      tabIndex={0}
      onMouseEnter={handleAgentState(true)}
      onMouseLeave={handleAgentState(false)}
    >
      <div className="displayStateSelect" tabIndex={0}>
        <div
          className="displayHoverButton"
          hidden={showGoAvailableButton}
          role="button"
          tabIndex={0}
        >
          <div
            tabIndex={0}
            aria-live="assertive"
            className="hoverButton"
            onClick={() => handleStatusDisplay()}
          >
            {!showGoAvailableButton && (
              <div
                tabIndex={0}
                style={{
                  width: 'inherit',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <span className="hoverButtonIconText">
                  <CcfAvailableIcon
                    className="iconAvailable"
                    viewBox="0 0 22 22"
                  />
                </span>
                <CcfTypography
                  variant="inherit"
                  className="hoverButtonText"
                  translationKey="available"
                />
              </div>
            )}
          </div>
        </div>

        <div className="stateSelected" tabIndex={0}>
          {showGoAvailableButton && (
            <>
              <div className="avatarMain">
                <CcfBadge
                  label="userAvatar"
                  overlap="circular"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  badgeContent={
                    agentCurrentStatus === 'Working' ? (
                      <CcfWorkingIcon className="badgeAvatarIcon"></CcfWorkingIcon>
                    ) : agentCurrentStatus === WORKINGOB ? (
                      <CcfWorkingIcon className="badgeAvatarIcon fillRed" />
                    ) : agentCurrentStatus !== AVAILABLE ? (
                      <CcfUnavailableIcon className="badgeAvatarIcon fillRed" />
                    ) : (
                      <CcfAvailableIcon className="badgeAvatarIcon fillGreen" />
                    )
                  }
                >
                  <CcfAvatar alt={initials} className="avatarHeader">
                    {initials}
                  </CcfAvatar>
                </CcfBadge>
              </div>

              <div
                aria-label={
                  agentCurrentStatus + ' (' + formatTimer(timer) + ')'
                }
              >
                {translate(agentCurrentStatus) + ' (' + formatTimer(timer) + ')'}
              </div>
            </>
          )}
        </div>
      </div>
      {!props?.agentStateForEmbeededView && showAllUnavailableStatus && (
        <div
          className="unavailableStatusSelection"
          tabIndex={0}
          onMouseEnter={handleAgentState(true)}
          onMouseLeave={handleAgentState(false)}
        >
          <div className="myProfile">
            <CcfAvatar alt={initials} className="myProfileAvatar">
              {initials}
            </CcfAvatar>
            <CcfTypography variant="inherit" className="myProfileText">
              {isAuthenticated ? accounts[0]?.username : 'My Profile'}
            </CcfTypography>
          </div>
          <div className="seperatorLine"></div>
          <div className="allStatus">
            {filteredFavStatus.length > 0 && (
              <CcfTypography
                variant="inherit"
                className="favoriteLabel"
                translationKey="favorites"
              />
            )}

            {filteredFavStatus.map((fav: Status) => (
              <ul className="unavailableStatusGrp" key={fav.id}>
                {handleIconForStatus(fav.value)}
                <li
                  className="unavailableStatus"
                  value={fav.value}
                  onClick={(e) => handleStatusSelection(fav.value)}
                >
                  {translate(fav.value)}
                </li>

                <span>
                  <CcfFavouriteIcon
                    className="starIcon"
                    onClick={() => dispatch(updateFavourite(fav))}
                  />
                </span>
              </ul>
            ))}
            <div className="seperatorLine"></div>
            <div>
              <CcfTypography
                variant="inherit"
                className="labelAllCodes"
                translationKey="allCodes"
              />

              {sortedStatus.map((other: Status) => (
                <ul className="unavailableStatusGrp" key={other.id}>
                  {handleIconForStatus(other.value)}
                  <li
                    className="unavailableStatus"
                    value={other.value}
                    onClick={() => {
                      handleStatusSelection(other.value);
                    }}
                    aria-label={other.value}
                  >
                    {translate(other.value)}
                  </li>
                  <span>
                    {other.isFavourite ? (
                      <CcfFavouriteIcon
                        className="starIcon"
                        onClick={() => dispatch(updateFavourite(other))}
                      />
                    ) : (
                      <CcfNotFavoriteIcon
                        className="starIcon"
                        onClick={() => dispatch(updateFavourite(other))}
                      />
                    )}
                  </span>
                </ul>
              ))}
            </div>
          </div>
          <div className="seperatorLine"></div>
          <div className="logout" id="logoutDiv" onClick={() => signOutUser()}>
            <CcfLogoutIcon
              className="logOutIcon"
              viewBox="0 0 20 20"
            ></CcfLogoutIcon>
            <CcfTypography className="logoutText" translationKey="logOut" />
          </div>
        </div>
      )}
    </section>
  );
}

export default CcfAgentState;
